import { useContext, useState } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { GetPreQueryMemberInfoResponse, ListProductsResponse, PreQueryMemberInfoPhoneItem, QueryMemberRequest, QueryMemberResponse } from '../../models/query-member';
import { post, get } from '../../core/client';
import MemberInfo from '../query-member/MemberInfo';
import Purchase from '../query-member/Purchase';
import OtpModal from './OtpModal';
import QueryMemberContextProvider, { QueryMemberContext } from './Context';
import { IsNullOrEmpty } from '../../core/utility';
import Input from '../../components/inputs/text-field-input';
import { ClearButton, CustomButton } from '../../components/buttons';
import PurchaseWithoutInfo from './PurchaseWithoutInfo';
import AuthManager, { UserRoles } from '../../core/oidc';


const QueryMemberComponent = () => {

    //context
    const { member, fillMember, fillProducts, arrangeShowOtpModal } = useContext(QueryMemberContext);

    //definitions
    const initialState = {
        queryType: 4,
        no: '',
    };

    //states
    const [state, setState] = useState<QueryMemberRequest>(initialState);
    const [memberLoaded, setMemberLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [buttonLoading, setButtonLoading] = useState(false);
    const [productsLoading, setProductsLoading] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [resultMessage, setResultMessage] = useState<string>('');

    const [phones, setPhones] = useState<PreQueryMemberInfoPhoneItem[]>([]);

    //methods
    const loadProducts = async () => {
        setProductsLoading(true)
        const response = await post<ListProductsResponse>('api/portal/list-products-by-quota', { tcknOrGoldenCode: state.no });
        setProductsLoading(false)
        if (response) {
            fillProducts(response);
        }
    };

    const handleGetPreQueryMemberInfo = async (no: string) => {

        if (!validate()) return;

        setMemberLoaded(false);
        setButtonLoading(true);
        setResultMessage('');

        const response = await post<GetPreQueryMemberInfoResponse>('api/portal/get-pre-query-member-info', { no });
        if (response) {
            setButtonLoading(false);

            if (!response.isExisted) {
                // no result
                setMemberLoaded(true);
                setResultMessage('KoçAilem üyeliği yoktur. Sorularınız ve detaylı bilgi için 444 0 447 KoçAilem Çağrı Merkezini arayabilirsiniz.');
                return;
            }

            if (response.isExisted && !response.isActive) {
                setMemberLoaded(true);
                setResultMessage('KoçAilem üyeliğiniz aktif değildir. Sorularınız ve detaylı bilgi için 444 0 447 KocAilem Çağrı Merkezini arayabilirsiniz.');
                return;
            }

            if (response.phones.length > 0) {
                const phones = response.phones.length > 1 ? [{ id: 0, text: 'Seçiniz' }, ...response.phones] : response.phones;
                setPhones(phones);
                arrangeShowOtpModal(true);
                return;
            }

            setMemberLoaded(true);
            setResultMessage('Doğrulama yapılacak bir telefon kaydı bulunmuyor');
            return;
        }
    }

    const queryMember = async (communicationId: number, smsCode: string) => {

        setLoading(true);
        const request = {
            queryType: state.queryType,
            no: state.no,
            communicationId: communicationId,
            smsCode: smsCode
        }
        const response = await post<QueryMemberResponse>('api/portal/av-member-query', request);
        if (response) {
            setMemberLoaded(true);

            if (response.memberAv) {
                loadProducts();
                fillMember(response.memberAv);
            }
            const message = !IsNullOrEmpty(response.errorMessage) ? response.errorMessage : response.description;
            setResultMessage(message);
        } else {
            fillMember(null);
            setMemberLoaded(true);
            setResultMessage('Sonuç bulunamadı');
        }
        setLoading(false);
    };

    const handleSmsCodeReceived = async (communicationId: number, smsCode: string) => {
        await queryMember(communicationId, smsCode);
    }

    const handleNoChange = (e: any) => {

        let result = String(e.target.value).replace(/\D/g, '');
        if (result.length > 11) {
            result = result.slice(0, 11);
        }

        setState(prevState => ({
            ...prevState,
            no: result,
        }));
    };

    const handleResetForm = () => {
        setState(initialState);
        fillMember(null);
        setMemberLoaded(false);
        setErrorText('');
        fillProducts(null);
    };

    const validate = (): boolean => {
        let result = true;
        setErrorText('');

        if (state.no === '') {
            result = false;
            setErrorText('Boş bırakılamaz');
        }
        else if (state.no.length !== 11) {
            result = false;
            setErrorText('11 karakter uzunluğunda olmalıdır');
        }

        return result;
    };

    const IsUserKampBayiCirosuz = () : boolean =>{
       const userRoles = AuthManager.getUserRoles();
       return userRoles === null ? false : userRoles.includes(UserRoles.CirosuzBayi);
    }

    return (
        <>
            <div className="page-header">
                <div className="page-title">
                    <h2>Üye Sorgulama</h2>
                </div>
            </div>
            <div className="page-content">
                <Grid container direction="column" alignItems="center">
                    <Box className="member-search" p={2}>
                        <Box className="form-field" mb={2}>
                            <Input
                                id="no"
                                name="no"
                                caption="TC kimlik No veya KoçAilem No giriniz"
                                patternFormat={{
                                    format: '###########'
                                }}
                                onChange={handleNoChange}
                                value={state.no}
                                errorText={errorText}
                            />
                        </Box>
                        <Grid container spacing={1} justifyContent="center">
                            <Grid item>
                                <CustomButton
                                    text="ÜYE SORGULA"
                                    className='ks-btn btn-primary'
                                    disabled={buttonLoading || loading}
                                    loading={buttonLoading || loading}
                                    onClick={async () => await handleGetPreQueryMemberInfo(state.no)}
                                />
                            </Grid>
                            <Grid item>
                                <ClearButton
                                    disabled={buttonLoading || loading || state.no === ''}
                                    onClick={() => handleResetForm()}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    {loading ? (
                        <Box pt={1} width="90%">
                            <Grid container xl spacing={3}>
                                <Grid item md={7}>
                                    <Skeleton animation="wave" height={60} />
                                    <Skeleton animation="wave" height={60} />
                                    <Skeleton animation="wave" height={60} />
                                    <Skeleton animation="wave" height={60} />
                                </Grid>
                                <Grid item md={5}>
                                    <Skeleton animation="wave" height="100%" />
                                </Grid>
                            </Grid>
                        </Box>
                    ) : null}
                    {memberLoaded ? (
                        member && member.goldenCode !== '' ? (
                            <Box p={2} pt={3} width="100%">
                                <Grid container xl spacing={3}>
                                    <Grid item md={7}>
                                        <div className="widget-area" style={{ height: '100%' }}>
                                            <MemberInfo />
                                        </div>
                                    </Grid>
                                    <Grid item md={5}>
                                        <div className="widget-area" style={{ height: '100%' }}>
                                            {IsUserKampBayiCirosuz() ?
                                                (<PurchaseWithoutInfo productLoading={productsLoading}
                                                    onNewPurchase={() => handleResetForm()} />)
                                                    :
                                                (<Purchase
                                                    productLoading={productsLoading}
                                                    onNewPurchase={() => handleResetForm()}
                                                />)
                                            }  
                                        </div>
                                    </Grid>
                                </Grid>
                            </Box>
                        ) : (
                            <Typography variant="subtitle1" display="block">{resultMessage}</Typography>
                        )
                    ) : null}
                </Grid>
            </div>
            <OtpModal
                phones={phones}
                onSend={handleSmsCodeReceived} />
        </>
    );
};

const QueryMember = () => (
    <QueryMemberContextProvider>
        <QueryMemberComponent />
    </QueryMemberContextProvider>
);

export default QueryMember;
